/**
 * NREL REopt Service
 * 
 * Handles all NREL REopt API interactions:
 * - Job submission
 * - Status polling
 * - Results retrieval
 * - Schema fetching
 */

import { apiPost, apiGet, handleApiResponse } from './api';

/**
 * Fetch the REopt input schema from NREL
 * @returns {Promise<Object>} REopt schema
 */
export async function fetchSchema() {
    const response = await apiGet('/schema');
    return handleApiResponse(response);
}

/**
 * Submit a scenario for optimization
 * @param {Object} scenario - REopt scenario payload
 * @returns {Promise<{run_uuid: string}>} Run UUID
 */
export async function submitScenario(scenario) {
    const response = await apiPost('/submit', scenario);
    return handleApiResponse(response);
}

/**
 * Get the status of an optimization run
 * @param {string} runUuid - Run UUID
 * @returns {Promise<Object>} Status and results
 */
export async function getStatus(runUuid) {
    const response = await apiGet(`/status/${runUuid}`);
    return handleApiResponse(response);
}

/**
 * Get the results of a completed optimization run
 * @param {string} runUuid - Run UUID
 * @returns {Promise<Object>} Full results
 */
export async function getResults(runUuid) {
    const response = await apiGet(`/results/${runUuid}`);
    return handleApiResponse(response);
}

/**
 * Calculate BAU (Business As Usual) utility bill
 * @param {Object} data - Bill calculation parameters
 * @returns {Promise<Object>} BAU bill details
 */
export async function calculateBauBill(data) {
    const response = await apiPost('/calculate_bau', data);
    return handleApiResponse(response);
}

/**
 * Get available load models
 * @returns {Promise<Object>} Load model information
 */
export async function getLoadModels() {
    const response = await apiGet('/load_models');
    return handleApiResponse(response);
}

/**
 * Poll for optimization completion
 * @param {string} runUuid - Run UUID
 * @param {Object} options - Polling options
 * @returns {Promise<Object>} Final results
 */
export async function pollForCompletion(runUuid, options = {}) {
    const {
        interval = 15000,
        timeout = 600000,
        onProgress = null,
    } = options;

    const startTime = Date.now();

    return new Promise((resolve, reject) => {
        const poll = async () => {
            try {
                const data = await getStatus(runUuid);
                const status = data.status || 'unknown';

                if (onProgress) {
                    onProgress(status, data);
                }

                // Check for completion
                if (['optimal', 'Optimal Obtained', 'completed'].includes(status)) {
                    resolve(data);
                    return;
                }

                // Check for failure
                if (['error', 'Error', 'failed', 'Failed', 'timed_out'].includes(status)) {
                    const errorMsg = data.messages?.errors?.join(', ') || 'Optimization failed';
                    reject(new Error(errorMsg));
                    return;
                }

                // Check timeout
                if (Date.now() - startTime > timeout) {
                    reject(new Error('Optimization timed out'));
                    return;
                }

                // Continue polling
                setTimeout(poll, interval);
            } catch (err) {
                // Don't stop on transient errors
                console.error('Polling error:', err);
                setTimeout(poll, interval);
            }
        };

        poll();
    });
}

export default {
    fetchSchema,
    submitScenario,
    getStatus,
    getResults,
    calculateBauBill,
    getLoadModels,
    pollForCompletion,
};
